import ExcelJS from "exceljs";
import { sortAndGroup } from "./campAttendanceSort";

const HEADER_FILL = "FFDCE6F1"; // light blue header (matches on-screen table)
const HEADER_FONT = "FF1F3864"; // dark navy header text
const BORDER_COLOR = "FF000000"; // thin black border on every cell
const TEXT_COLOR = "FF333333";
const FONT_NAME = "Calibri";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

// Static columns (1-6). Widths/alignment match the reference sheet.
const STATIC_COLUMNS = [
  { header: "Camp Type", field: "camp_type", width: 7.83, align: "left" },
  { header: "Course", field: "course_abbr", width: 17.33, align: "left" },
  { header: "Student ID", field: "student_id", width: 9.33, align: "center" },
  { header: "Student Name", field: "student_name", width: 15.83, align: "left" },
  { header: "Parent Name", field: "parent_name", width: 19.66, align: "left" },
  { header: "Phone Number", field: "phone", width: 12.83, align: "center" },
];

const WEEKDAY_COL_WIDTH = 4.33;

const allBorders = {
  top: { style: "thin", color: { argb: BORDER_COLOR } },
  left: { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  right: { style: "thin", color: { argb: BORDER_COLOR } },
};

function styleHeaderCell(cell) {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
  cell.font = { name: FONT_NAME, bold: true, size: 11, color: { argb: HEADER_FONT } };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.border = allBorders;
}

export const generateCampAttendanceWorkbook = async (reports) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Camp Attendance", {
    views: [{ state: "frozen", ySplit: 2 }], // keep the header visible while scrolling
  });

  const totalCols = STATIC_COLUMNS.length + WEEKDAYS.length * 2; // 16

  // Column widths
  STATIC_COLUMNS.forEach((col, i) => {
    worksheet.getColumn(i + 1).width = col.width;
  });
  for (let c = STATIC_COLUMNS.length + 1; c <= totalCols; c++) {
    worksheet.getColumn(c).width = WEEKDAY_COL_WIDTH;
  }

  // ---- Header (rows 1-2) ----
  STATIC_COLUMNS.forEach((col, i) => {
    const colIdx = i + 1;
    worksheet.mergeCells(1, colIdx, 2, colIdx);
    worksheet.getCell(1, colIdx).value = col.header;
  });

  WEEKDAYS.forEach((day, i) => {
    const inCol = STATIC_COLUMNS.length + 1 + i * 2;
    const outCol = inCol + 1;
    worksheet.mergeCells(1, inCol, 1, outCol);
    worksheet.getCell(1, inCol).value = day;
    worksheet.getCell(2, inCol).value = "In";
    worksheet.getCell(2, outCol).value = "Out";
  });

  // Style both header rows across all columns
  for (let r = 1; r <= 2; r++) {
    for (let c = 1; c <= totalCols; c++) {
      styleHeaderCell(worksheet.getCell(r, c));
    }
  }
  worksheet.getRow(1).height = 24;
  worksheet.getRow(2).height = 18;

  // ---- Data rows ----
  const rows = sortAndGroup(reports);
  let excelRow = 3;

  rows.forEach((row) => {
    if (row.__groupStart) {
      // Light-blue band (header style) across all columns to break course groups.
      for (let c = 1; c <= totalCols; c++) {
        styleHeaderCell(worksheet.getCell(excelRow, c));
      }
      excelRow += 1;
    }

    const wsRow = worksheet.getRow(excelRow);
    wsRow.height = 20;

    STATIC_COLUMNS.forEach((col, i) => {
      const cell = wsRow.getCell(i + 1);
      cell.value = row[col.field] ?? "";
      cell.alignment = { vertical: "middle", horizontal: col.align };
      cell.font = { name: FONT_NAME, size: 11, color: { argb: TEXT_COLOR } };
    });

    for (let c = 1; c <= totalCols; c++) {
      wsRow.getCell(c).border = allBorders;
    }

    excelRow += 1;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const downloadCampAttendanceSheet = async (reports, branchName) => {
  const blob = await generateCampAttendanceWorkbook(reports);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${branchName} - Camp Attendance Sheet.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
